import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function CodeSplittingPage(): Node {
  return html`
    <div>
      <h1>Code Splitting</h1>
      <p>Use <code>lazy()</code> with the Router for automatic route-based code splitting. Each page is loaded only when the user navigates to it.</p>

      <h2>Route-Based Splitting</h2>
      ${CodeBlock(`import { Router, lazy, Suspense, html, mount } from 'onefold';
import { Suspense } from 'onefold/suspense';

// Each import() creates a separate chunk at build time
const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const Analytics = lazy(() => import('./pages/Analytics'));

function App(): Node {
  return html\`
    <div>
      \${Suspense(
        () => Router([
          { path: '/', view: () => Home() },
          { path: '/dashboard', view: () => Dashboard() },
          { path: '/settings', view: () => Settings() },
          { path: '/analytics', view: () => Analytics() },
        ]),
        () => html\`<div class="loading">Loading...</div>\`
      )}
    </div>
  \`;
}

mount(App(), document.getElementById('app')!);`)}

      <h2>Build Configuration</h2>
      <p>Most bundlers (esbuild, Vite, webpack) split dynamic imports into separate chunks automatically:</p>
      ${CodeBlock(`// esbuild
import esbuild from 'esbuild';

esbuild.build({
  entryPoints: ['src/main.ts'],
  bundle: true,
  splitting: true,    // enables code splitting
  format: 'esm',     // required for splitting
  outdir: 'dist',
});`)}

      ${Callout('Code splitting only works with ESM output format. Make sure your build tool outputs ES modules.')}

      <h2>Chunk Naming</h2>
      ${CodeBlock(`// Output:
// dist/main.js         — entry point + router
// dist/chunk-Home.js   — Home page
// dist/chunk-Dashboard.js — Dashboard page
// dist/chunk-Settings.js  — Settings page`)}

      <h2>Preloading</h2>
      <p>Preload likely-needed chunks on hover or idle:</p>
      ${CodeBlock(`// Preload on link hover
function NavLink(href: string, label: string, loader: () => Promise<any>): Node {
  return html\`
    <a
      href=\${href}
      onmouseenter=\${() => loader()}
      onclick=\${(e: Event) => { e.preventDefault(); navigate(href); }}
    >\${label}</a>
  \`;
}

// Usage
NavLink('/dashboard', 'Dashboard', () => import('./pages/Dashboard'));`)}

      <h2>Bundle Analysis</h2>
      <p>Add <code>metafile: true</code> to esbuild to analyze chunk sizes:</p>
      ${CodeBlock(`const result = await esbuild.build({
  entryPoints: ['src/main.ts'],
  bundle: true,
  splitting: true,
  format: 'esm',
  outdir: 'dist',
  metafile: true,
});

// Write analysis
const text = await esbuild.analyzeMetafile(result.metafile);
console.log(text);`)}
    </div>
  `;
}
